import React, { useContext, useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, FormikHelpers, FormikErrors } from 'formik';
import { Redirect } from 'react-router-dom';
import { Environment } from 'src/Environment';
import { Colors } from 'src/common';
import { authenticate, setOtpForSession } from 'src/services/user.service';
import {
  paymentsDashboardRoute,
  resetPasswordMerchantIdRoute,
  setPasswordRoute,
  firstTimeLoginOtpRoute,
  deviceActivationRoute,
} from 'src/routing/routes';
import { FloatingInput, ShapesDecorativeBorder, ButtonLink, DotLoader } from 'src/components';
import TealiumEvents, {
  SetLoginType,
  UnsetResetJourney,
  SetResetJourney,
} from 'src/helpers/tealium-events';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SelfServiceFormBackground } from '../self-service-form-background/self-service-form-background';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { FormContainer } from '../form-container/form-container';
import { ImageCache } from 'src/components/image-cache/image-cache';
import {
  CactusImage,
  CashAdvanceApproved,
  CashAdvanceCongrats,
  CashAdvanceRoadSign,
  ComingSoonLabel,
  Error,
  FallenIceCream,
  FallingBags,
  HandCash,
  InputErrorIcon,
  RocketImage,
  TapPay,
  TickedCheckboxPortal,
  UntickedCheckboxPortal,
  CloseIcon,
} from 'src/assets/images';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Button } from '@vfs-digital-channels/ns-react-components';
import { CacheContext } from 'src/features/common';

type LoginFormValues = {
  merchantId: string;
  password: string;
};

export default function Login() {
  const cacheStore = useContext(CacheContext);
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const handleTechnicalError = useTechnicalErrorHandler();
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const [initialValues, setInitialValues] = useState<LoginFormValues>({
    merchantId: '',
    password: '',
  });
  const topOfPage = useRef<HTMLDivElement>(null);

  const redirectToOnboardingRoute = Environment.current.uiHost + '/onboarding/pos/';
  const redirectToDeviceActivationRoute = Environment.current.uiHost + deviceActivationRoute;

  useEffect(() => {
    if (MiniAppEvents.isInMiniApp()) {
      const siteTitle = document.getElementById('siteTitle');
      if (siteTitle) {
        siteTitle.innerText = 'Business Portal';
      }
    }
  }, []);

  useEffect(() => {
    if (inMiniProgram) {
      MiniAppEvents.sendMessage({
        messageType: 'get_msisdn',
      });

      MiniAppEvents.listenForMessage((observer) => {
        const message = MiniAppEvents.readMessage();
        if (message.type === 'get_msisdn') {
          if (message.data) {
            const newValues = { ...initialValues, merchantId: message.data };
            setInitialValues(newValues);
          }
          observer.disconnect();
        }
      });
    }
  }, [inMiniProgram]);

  useEffect(() => {
    // Needed when user is on the login page, navigates to register device
    // then navigates back to login and navigates to choose solution
    cacheStore.clear();
    TealiumEvents.loginPageLoadNewStyle();
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const formContent = () => {
    return (
      <section className="self-service-login-form">
        <ImageCache
          images={[
            TapPay,
            CactusImage,
            CashAdvanceApproved,
            FallingBags,
            HandCash,
            CashAdvanceCongrats,
            CashAdvanceRoadSign,
            ComingSoonLabel,
            Error,
            FallenIceCream,
            InputErrorIcon,
            RocketImage,
            UntickedCheckboxPortal,
            TickedCheckboxPortal,
            CloseIcon,
          ]}
        />
        <Formik<LoginFormValues>
          initialValues={initialValues}
          validate={validateLoginForm}
          onSubmit={submitLoginForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          {({ isSubmitting, values }) => (
            <FormContainer>
              <Form>
                <div className="pt-10 text-center">
                  <div className="pt-5 pb-4 text-4xl text-grey-600" ref={topOfPage}>
                    Log in to your account
                  </div>
                </div>
                <div className="pb-8 space-y-8">
                  <div className="space-y-5">
                    <Field
                      name="merchantId"
                      type="text"
                      placeholder="Merchant ID"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="top-field"
                      spacing="has-margin-top-5"
                      automationIdPrefix="Login_Input_MerchantID"
                      autoFocus
                    />
                    <Field
                      name="password"
                      type="password"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      className="bottom-field"
                      fieldType="standard"
                      placeholder="Password"
                      automationIdPrefix="Login_Input_Password"
                    />
                  </div>
                  <div className="space-y-[10px]">
                    <Button
                      className="w-full !py-6 !text-base !font-medium !bg-red-600 disabled:!bg-grey-100"
                      type="submit"
                      disabled={isSubmitting || !isFormValid(values)}
                    >
                      {isSubmitting ? <DotLoader color={Colors.BlueGray4} /> : 'Login'}
                    </Button>
                    <div className="text-center">
                      <ButtonLink
                        automationid="Login_Link_Request_Reset"
                        onClick={() => {
                          if (!isSubmitting) {
                            TealiumEvents.loginClickForgotPassword(resetPasswordMerchantIdRoute);
                            setRedirectToRoute(resetPasswordMerchantIdRoute);
                            UnsetResetJourney();
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        Forgot password?
                      </ButtonLink>
                    </div>
                    <div className="text-center">
                      <span className="text-sm text-grey-500">First time member?</span>
                      &nbsp;
                      <ButtonLink
                        automationid="Login_Link_First_Time_Login"
                        className="font-bold"
                        onClick={() => {
                          TealiumEvents.loginClickFirstTimeMember(firstTimeLoginOtpRoute);
                          setRedirectToRoute(firstTimeLoginOtpRoute);
                          SetResetJourney('first time login');
                        }}
                        disabled={isSubmitting}
                      >
                        Click here
                      </ButtonLink>
                    </div>
                  </div>
                  <div className="space-y-[10px]">
                    <p className="text-base font-bold text-center text-grey-500">
                      Looking for something else?
                    </p>

                    <div className="text-center">
                      <span className="text-sm text-grey-500">Already have a card machine?</span>
                      &nbsp;
                      <ButtonLink
                        className="font-bold"
                        onClick={() => {
                          window.location.href = redirectToDeviceActivationRoute;
                        }}
                        disabled={isSubmitting}
                      >
                        Register here
                      </ButtonLink>
                    </div>
                    <div className="text-center">
                      <span className="text-sm text-grey-500">View all our payment solutions.</span>
                      &nbsp;
                      <ButtonLink
                        className="font-bold"
                        onClick={() => {
                          window.location.href = redirectToOnboardingRoute;
                        }}
                        disabled={isSubmitting}
                      >
                        Click here
                      </ButtonLink>
                    </div>
                  </div>
                </div>
              </Form>
            </FormContainer>
          )}
        </Formik>
      </section>
    );
  };

  return (
    <div className="broker-login">
      <SelfServiceFormBackground className="">
        <ShapesDecorativeBorder>{formContent()}</ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );

  function handleSuccessfullFirstTimeLogin(password: string) {
    TealiumEvents.loginClickLogin(setPasswordRoute);

    SetResetJourney('first time login');
    setOtpForSession(password);
    setRedirectToRoute(setPasswordRoute);
  }

  function handleSuccessfullLogin(values: LoginFormValues) {
    TealiumEvents.loginClickLogin(values.merchantId);
    setRedirectToRoute(paymentsDashboardRoute);
  }

  function handleBadLogin() {
    TealiumEvents.onHandledErrorFormSelfService('Invalid Merchant ID or password', '401');
    toastMessageServiceSingleton.addError(
      'Invalid Merchant ID or password',
      null,
      '401',
      true,
      null,
      true,
    );
  }

  async function submitLoginForm(values: LoginFormValues, helpers: FormikHelpers<LoginFormValues>) {
    const { setSubmitting, resetForm } = helpers;
    const params = {
      ...values,
      showToastOnError: false,
      suppressTealiumEvent: true,
    };

    // @ts-ignore
    const { firstTimeLogin, error } = await authenticate(params);
    SetLoginType('Password');
    setSubmitting(false);

    switch (error) {
      case null:
        firstTimeLogin
          ? handleSuccessfullFirstTimeLogin(values.password)
          : handleSuccessfullLogin(values);
        break;
      case 'Your password or merchant ID is incorrect':
        handleBadLogin();
        resetForm();
        break;
      default:
        // @ts-ignore
        handleTechnicalError('unrecognized error received in response to login POST request');
    }
  }
}

const isFormValid = (values) => values.merchantId && values.password;

function validateLoginForm(values) {
  const errors: FormikErrors<LoginFormValues> = {};
  if (!values.merchantId) {
    errors.merchantId = 'Your merchant ID is incorrect';
  }

  if (!values.password) {
    errors.password = 'Your password is incorrect';
  }
  return errors;
}
